import { Label } from 'shared/ui/label';
import { Input } from 'shared/ui/input';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { FileWithPath } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadCode, uploadData } from 'features/upload-files/api';
import { useApiStatusToast } from 'shared/hooks/useApiStatusToast';
import { CloudUpload, FileCheck, FileX, Loader } from 'lucide-react';
import type { UploadedFile } from 'features/upload-files/ui/uploaded-files';
import { UploadedFiles } from 'features/upload-files/ui/uploaded-files';
import { isMobile } from 'react-device-detect';
import { Button } from 'shared/ui/button';
import { Progress } from 'shared/ui/progress';
import { filesQueryKeys } from '@/enteties/file-storage/api/file-storage.query-key';
import * as m from '@paraglide/messages';
interface UploadFilesFieldsProps {
  path: string;
  tab: 'CODE' | 'DATA';
  slug: string;
}
export const UploadFilesFields = ({
  path,
  tab,
  slug
}: UploadFilesFieldsProps) => {
  const queryClient = useQueryClient();
  const commitMessage = useRef(null);
  const [success, error] = useApiStatusToast();
  const [percent, setProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const {
    mutate: uploadCodeFn,
    isPending,
    isSuccess,
    isError
  } = useMutation({
    mutationFn: ({
      formData,
      slug
    }: {
      formData: FormData;
      slug: string;
    }) => uploadCode(formData, setProgress, slug),
    onSuccess: async (_, variables) => {
      const acceptedFls = variables.formData.getAll('files') as File[];
      const uploadedFiles: UploadedFile[] = acceptedFls.map(file => ({
        path: file.name,
        name: file.name
      }));
      setUploadedFiles(prev => [...prev, ...uploadedFiles]);
      await queryClient.invalidateQueries({
        queryKey: filesQueryKeys.projectFilesGit(slug, 'CODE', 'master', path)
      });
      success({
        title: m.upload_dialog_field_upload_success()
      });
    },
    onError: e => {
      error({
        description: e.message === 'Network Error' ? 'Payload too big' : e.message
      });
    }
  });
  const {
    mutate: uploadDataFn,
    isPending: dataPending,
    isSuccess: isDataSuccess,
    isError: isDataError
  } = useMutation({
    mutationFn: ({
      formData,
      slug
    }: {
      formData: FormData;
      slug: string;
    }) => uploadData(formData, setProgress, slug),
    onSuccess: async (_, variables) => {
      const acceptedFls = variables.formData.getAll('files') as File[];
      const uploadedFiles: UploadedFile[] = acceptedFls.map(file => ({
        path: file.name,
        name: file.name
      }));
      setUploadedFiles(prev => [...prev, ...uploadedFiles]);
      await queryClient.invalidateQueries({
        queryKey: filesQueryKeys.projectFilesTab(slug, 'DATA')
      });
      success({
        title: m.upload_dialog_field_upload_success()
      });
    },
    onError: e => {
      error({
        description: e.message
      });
    }
  });
  const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
    const formData = new FormData();
    formData.append('path', path);
    acceptedFiles.forEach(file => {
      formData.append('files', file, file.path);
    });
    if (tab === 'CODE') {
      if (commitMessage.current) {
        // @ts-ignore
        formData.append('commitMessage', commitMessage.current.value);
      }
      formData.append('path', path);
      formData.append('branch', 'master');
      uploadCodeFn({
        formData,
        slug
      });
    } else if (tab === 'DATA') {
      uploadDataFn({
        formData,
        slug
      });
    }
  }, [path, tab, uploadCodeFn, slug, uploadDataFn]);
  const successUpload = useMemo(() => isSuccess || isDataSuccess, [isDataSuccess, isSuccess]);
  const errorUpload = useMemo(() => isError || isDataError, [isDataError, isError]);
  const pendingUpload = useMemo(() => isPending || dataPending, [dataPending, isPending]);
  const {
    getRootProps,
    getInputProps,
    open
  } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true
  });
  return <div className="" data-sentry-component="UploadFilesFields" data-sentry-source-file="upload-files-fields.tsx">
      {tab === 'CODE' && <div className="grid  items-center gap-1.5">
          <Label htmlFor="commitMessage">Commit message:</Label>
          <Input id="commitMessage" ref={commitMessage} defaultValue="Initial commit" />
        </div>}
      <div className="mt-6 mb-3 size-full">
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          {isMobile ? <div className="my-4 flex flex-col gap-4">
              <Button size="sm" onClick={open} className="w-full" type="button">
                Загрузка только файла
              </Button>
              {(isPending || dataPending) && <div className="flex items-center gap-4">
                  <Progress value={percent} />
                  <p className="text-body3">{percent}&#37;</p>
                </div>}
            </div> : <div className="flex h-full items-center justify-center rounded-xl border-2 border-dashed align-middle bg-sidebar py-10 px-14">
              <div className="flex flex-col  items-center justify-center gap-2">
                {successUpload ? <FileCheck className="size-6 text-status-success" /> : errorUpload ? <FileX className="size-6 text-status-error" /> : pendingUpload ? <Loader className="size-6 animate-spin [animation-duration:2s]" /> : <CloudUpload className="size-6 text-primary" />}
                <h1 className="text-center text-title3">
                  {m.upload_dialog_field_upload_text()}
                </h1>
                <div className="space-y-1">
                  <h3 className="text-center text-body2">
                    {m.upload_dialog_field_upload_maxSize()}{' '}
                    <span className="font-medium">
                      200 {m.upload_dialog_field_upload_mb()}
                    </span>
                  </h3>
                  <h3 className="text-center text-body2">
                    {m.upload_dialog_field_upload_zip()}
                  </h3>
                </div>
              </div>
            </div>}
        </div>
      </div>
      <UploadedFiles uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles} slug={slug} data-sentry-element="UploadedFiles" data-sentry-source-file="upload-files-fields.tsx" />
    </div>;
};